import { DropzoneField } from '@lfg/ui/components';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';

import { AdminInput } from '../AdminTextField';
import { StepAccordion } from '../StepAccordion';

interface TokenConfigurationProps {
  isDisabled?: boolean;
}

export function TokenConfiguration({ isDisabled }: TokenConfigurationProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { hasDeployed } = useDeployment();

  return (
    <StepAccordion isError={!!errors.node} title={'Core NFT Configuration'} index={3}>
      <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }} mb={3}>
        Each core license is minted as an ERC-721 NFT, representing unique ownership. Learn how naming, symbols, and
        assets are used to customize your core NFTs. Visit the{' '}
        <a
          href={'https://link.lfg.inc/kb-coresale-nodenftconfiguration'}
          target={'_blank'}
          style={{ color: 'white', textDecoration: 'underline' }}>
          Configure Core NFT
        </a>{' '}
        article for more info.
      </Typography>

      <Grid container columnSpacing={2}>
        <Grid size={12}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Core Name
            </Typography>
            <Controller
              name='node.name'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={hasDeployed || isDisabled}
                  isError={!!errors?.node?.name}
                  errorMsg={errors.node?.name?.message}
                  placeholder={'Enter name'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Symbol
            </Typography>
            <Controller
              name='node.symbol'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={hasDeployed || isDisabled}
                  isError={!!errors?.node?.symbol}
                  errorMsg={errors.node?.symbol?.message}
                  placeholder={'Enter symbol name'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Core Description
            </Typography>
            <Controller
              name='node.description'
              control={control}
              render={({ field }) => (
                <AdminInput
                  rows={5}
                  readOnly={hasDeployed || isDisabled}
                  inputHeight='140px'
                  multiline
                  isError={!!errors?.node?.description}
                  errorMsg={errors.node?.description?.message}
                  placeholder={'Give a short description about the project’s node...'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      padding: 0,
                    },
                  }}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Asset
            </Typography>
            <Controller
              name={'node.asset'}
              control={control}
              rules={{ required: 'Please upload at least one file' }}
              render={({ field }) => (
                <DropzoneField
                  disabled={hasDeployed || isDisabled}
                  maxFiles={1}
                  containerSx={{
                    border: `1px dashed ${errors.node?.asset ? colors.error : colors.neutral015}`,
                    backgroundColor: colors.neutral03,
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    maxHeight: '200px',
                  }}
                  onDrop={field.onChange}
                  files={field.value as unknown as File[]}
                  onClear={() => field.onChange([])}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'image/gif': [],
                  }}
                />
              )}
            />
            {errors.node?.asset ? (
              <Typography color={colors.error} fontSize={'14px'} paddingTop={1} variant='h6'>
                {errors.node?.asset?.message}
              </Typography>
            ) : null}
            <ul className='ml-[-28px] flex gap-x-8 items-center text-sm text-[#9C9BA3]'>
              <li>Supported formats: jpeg, png, gif</li>
              <li>Maximum file size: 10MB </li>
            </ul>
          </Box>
        </Grid>
      </Grid>
    </StepAccordion>
  );
}
